import * as zod from 'zod';
import type { GetSuggestionsInputs } from './getSuggestions';
import {
  DEFAULT_WEIGHT_PARAMS,
  getScenarioImprovementsParamDefaults,
  orderSelectionParamsSchema,
  scenarioImprovementsParamsSchema,
  weightParamsSchema,
} from '../../ScenarioImprovementParams';
import { OPTION_NO_CARGO_ALLOCATIONS } from './Suggestions';

export enum Objective {
  MinimizeCost = 'min_cost',
  /** VCP = Voyage Cargo Performance */
  MaximizeVCP = 'max_vcp',
  ShortestDistance = 'min_distance',
  ShortestBallast = 'min_ballast',
  MaximizeTCE = 'max_tce',
  MinimizeCostPerTonMile = 'min_cost_per_tonmile',
}

/** Parameters for the external solver when voyageType is 'entire_scenario' */
export const externalSolverSuggestionParamsSchema = zod.object({
  /** Max solver time in seconds */
  maxTime: weightParamsSchema.shape.maxTime.default(
    DEFAULT_WEIGHT_PARAMS.maxTime,
  ),
  /** Time violation weight */
  timeViolation: zod.number().min(0).default(DEFAULT_WEIGHT_PARAMS.time),
  /** Capacity violation weight */
  capacityViolation: zod
    .number()
    .min(0)
    .default(DEFAULT_WEIGHT_PARAMS.capacity),
  /** Available ton miles penalty */
  atm: weightParamsSchema.shape.atm.default(DEFAULT_WEIGHT_PARAMS.atm),
  /** Demand ton miles penalty - actually a reward so negative values */
  dtm: weightParamsSchema.shape.dtm.default(DEFAULT_WEIGHT_PARAMS.dtm),
});

export type ExternalSolverSuggestionParams = zod.infer<
  typeof externalSolverSuggestionParamsSchema
>;

export const DEFAULT_EXTERNAL_SOLVER_SUGGESTION_PARAMS: ExternalSolverSuggestionParams =
  {
    maxTime: DEFAULT_WEIGHT_PARAMS.maxTime,
    timeViolation: DEFAULT_WEIGHT_PARAMS.time,
    capacityViolation: DEFAULT_WEIGHT_PARAMS.capacity,
    atm: DEFAULT_WEIGHT_PARAMS.atm,
    dtm: DEFAULT_WEIGHT_PARAMS.dtm,
  };

export const voyageSuggestionParamsSchema = zod.object({
  ...scenarioImprovementsParamsSchema.shape.seaber.shape,
  ...orderSelectionParamsSchema.shape,

  voyageType: zod.enum([
    'new_voyage_between',
    'new_voyage_open_position',
    'new_or_existing_voyage',
    'existing_voyage',
    'entire_scenario',
  ]),
  vesselType: zod.enum(['shown', 'all', 'selected', 'shortlist']),
  objective: zod.nativeEnum(Objective),
  numOrders: zod.number().min(1),
  computationBudget: zod.number().min(0.1),
  /** How much total delay allowed with no penalty */
  toleratedDelayH: zod.number().min(0),
  /** How much total delay allowed before combination is discarded */
  maxAllowedTotalDelayH: zod.number().min(0),
  /** How much total idle allowed with no penalty */
  toleratedIdleH: zod.number().min(0),

  firstAllowedTime: zod.number().default(() => Date.now()),
  lastAllowedTime: zod.number().nullable().default(null),

  maxBallastDistanceNm: zod.number().default(1000),

  externalSolverParams: externalSolverSuggestionParamsSchema.default(
    DEFAULT_EXTERNAL_SOLVER_SUGGESTION_PARAMS,
  ),
});

export type VoyageSuggestionParams = zod.infer<
  typeof voyageSuggestionParamsSchema
>;

export const getVoyageSuggestionParamDefaults: () => GetSuggestionsInputs =
  () => ({
    ...getScenarioImprovementsParamDefaults().seaber,
    orderSelection: 'all',
    objective: Objective.MaximizeVCP,
    orderTypes: {
      coaPreliminaries: true,
      fixedCoas: true,
      fixedSpots: true,
      inquiries: true,
    },
    voyageType: 'new_voyage_open_position',
    vesselType: 'shown',
    numOrders: 1,
    computationBudget: 1,
    toleratedDelayH: 3 * 24,
    maxAllowedTotalDelayH: 7 * 24,
    toleratedIdleH: 14 * 24,
    maxBallastDistanceNm: 1000,

    firstAllowedTime: Date.now(),
    lastAllowedTime: null,

    externalSolverParams: DEFAULT_EXTERNAL_SOLVER_SUGGESTION_PARAMS,

    addToShipmentId: null,
    insertAfter: null,
    insertBefore: null,
    delayUntilLD: false,
    allocationOptions: OPTION_NO_CARGO_ALLOCATIONS,
    violationBasis: 'eta',
    specialRequirements: new Map(),
  });
